import { lazy, Suspense, useEffect, useRef } from 'react';
import { useMediaSlot } from '../../lib/MediaContext';
import { resolveMediaUrl, getVideoEmbedUrl } from '../../lib/mediaApi';
import ImagePlaceholder from './ImagePlaceholder';
import AiPhotoBadge from './AiPhotoBadge';
import PhotoCaptionOverlay from './PhotoCaptionOverlay';
import { cn } from '../../lib/cn';

/** The `autoplay` HTML attribute alone is unreliable once the <video> mounts
 * before its `src` is known (the normal case here — media slots load async)
 * and then gets it applied on a later render: browsers don't consistently
 * re-run autoplay when `src` changes on an already-mounted element. Calling
 * .play() explicitly once a src is set is the robust fix. */
function AutoplayVideo({ src, className, ariaLabel }: { src: string; className?: string; ariaLabel: string }) {
  const ref = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    ref.current?.play().catch(() => {});
  }, [src]);
  return <video ref={ref} src={src} muted loop playsInline preload="auto" aria-label={ariaLabel} className={className} />;
}

// lottie-react bundles the full lottie-web engine, only actually needed on
// the handful of Lottie slots — lazy so it ships as its own chunk instead of
// bloating MediaImage's, which nearly every page uses.
const LottieMedia = lazy(() => import('./LottieMedia'));

interface MediaImageProps {
  slotKey: string;
  label: string;
  height?: number | string;
  className?: string;
  rounded?: boolean;
  /** Set for the one or two images visible above the fold on first paint
   * (e.g. the hero) so the browser fetches them eagerly at high priority
   * instead of lazily — improves LCP. Everything else defaults to lazy. */
  priority?: boolean;
  /** 'cover' (default) fills a fixed-height box, cropping to fit — right
   * for photography where framing is flexible. 'contain' instead sizes
   * the box to the image's own aspect ratio so nothing gets cropped —
   * use for documents/certificates/scans where the full page matters.
   * `height` is ignored in 'contain' mode except as the placeholder's
   * height before a real photo is uploaded. */
  fit?: 'cover' | 'contain';
}

/** Strips the internal "photo — " / "video — " placeholder-label prefix so
 * the alt text read by screen readers and search engines is clean
 * descriptive text, not admin-authoring shorthand. */
function cleanAlt(label: string): string {
  return label.replace(/^(photo|video)\s*[—-]\s*/i, '');
}

/** Drop-in for ImagePlaceholder — renders the real admin-uploaded photo for
 * this slot once one exists, falling back to the design placeholder until then. */
export default function MediaImage({ slotKey, label, height = 240, className, rounded = true, priority = false, fit = 'cover' }: MediaImageProps) {
  const slot = useMediaSlot(slotKey);
  if (!slot?.file_path) {
    return <ImagePlaceholder label={label} height={height} className={className} rounded={rounded} />;
  }

  if (slot.media_type === 'map') {
    return (
      <div className="relative" style={{ height: typeof height === 'number' ? `${height}px` : height }}>
        <iframe
          src={slot.file_path}
          title={cleanAlt(label)}
          loading={priority ? 'eager' : 'lazy'}
          referrerPolicy="no-referrer-when-downgrade"
          className={cn('w-full h-full border-0', rounded && 'rounded-md', className)}
        />
      </div>
    );
  }

  if (slot.media_type === 'video') {
    // Behaves like an animated photo, not a "video with controls" — always
    // autoplaying, muted (required for autoplay in every browser), and
    // looping, with no play/pause/volume UI. Unlike PlayableVideo (used for
    // real watch-it videos elsewhere), this never needs the one-at-a-time
    // playback registry since it's silent/ambient rather than something a
    // visitor "plays".
    //
    // Lottie (.json) animations are piggybacked onto the 'video' media_type
    // rather than adding a new ENUM value + migration — detected by file
    // extension since a Lottie file is otherwise indistinguishable from any
    // other upload at the DB-column level.
    if (slot.file_path.toLowerCase().endsWith('.json')) {
      return (
        <div className="relative" style={{ height: typeof height === 'number' ? `${height}px` : height }}>
          <Suspense fallback={null}>
            <LottieMedia
              src={resolveMediaUrl(slot.file_path)}
              className={cn('w-full h-full', rounded && 'rounded-md overflow-hidden', className)}
            />
          </Suspense>
        </div>
      );
    }
    const embedUrl = getVideoEmbedUrl(slot.file_path);
    return (
      <div className="relative" style={{ height: typeof height === 'number' ? `${height}px` : height }}>
        {embedUrl ? (
          <iframe
            src={embedUrl}
            title={cleanAlt(label)}
            allow="autoplay; encrypted-media"
            loading={priority ? 'eager' : 'lazy'}
            className={cn('w-full h-full border-0', rounded && 'rounded-md', className)}
          />
        ) : (
          <AutoplayVideo
            src={resolveMediaUrl(slot.file_path)}
            ariaLabel={cleanAlt(label)}
            className={cn('w-full h-full object-cover', rounded && 'rounded-md', className)}
          />
        )}
      </div>
    );
  }

  if (fit === 'contain') {
    // className (e.g. a max-width cap + centering) goes on the wrapper, not
    // the img — the wrapper is what PhotoCaptionOverlay's absolute overlay
    // is sized against, so if the img were narrower than a full-width
    // wrapper, the caption band would stretch past the photo's real edges.
    return (
      <div className={cn('relative', className)}>
        <img
          src={resolveMediaUrl(slot.file_path)}
          alt={cleanAlt(label)}
          loading={priority ? 'eager' : 'lazy'}
          fetchPriority={priority ? 'high' : 'auto'}
          decoding={priority ? 'sync' : 'async'}
          className={cn('block w-full h-auto', rounded && 'rounded-md')}
        />
        <PhotoCaptionOverlay text={slot.caption} />
        {!!slot.ai_enhanced && <AiPhotoBadge />}
      </div>
    );
  }

  return (
    <div className="relative" style={{ height: typeof height === 'number' ? `${height}px` : height }}>
      <img
        src={resolveMediaUrl(slot.file_path)}
        alt={cleanAlt(label)}
        loading={priority ? 'eager' : 'lazy'}
        fetchPriority={priority ? 'high' : 'auto'}
        decoding={priority ? 'sync' : 'async'}
        className={cn('w-full h-full object-cover', rounded && 'rounded-md', className)}
      />
      <PhotoCaptionOverlay text={slot.caption} />
      {!!slot.ai_enhanced && <AiPhotoBadge />}
    </div>
  );
}
